/**
 * @description slate 插件 - selection 相关
 * @author wangfupeng
 */

import {
  Editor, Location, Node, Point,
  Range,
} from 'slate'

import { getPositionByNode, getPositionBySelection } from '../../menus/helpers/position'
import { EDITOR_TO_SELECTION } from '../../utils/weak-maps'
import { DomEditor } from '../dom-editor'
import { IDomEditor } from '../interface'

export const withSelection = <T extends Editor>(editor: T) => {
  const e = editor as T & IDomEditor
  const { select, deselect, move } = e

  type MoveOptions = Parameters<Editor['move']>[0]

  // 选中
  e.select = (at: Location) => {
    select(at)
  }

  // 取消选中
  e.deselect = () => {
    const { selection } = e
    const root = DomEditor.findDocumentOrShadowRoot(e)
    const domSelection = root.getSelection()

    const selectionBelongsToEditor = domSelection
      && domSelection.rangeCount > 0
      && DomEditor.hasDOMNode(e, domSelection.anchorNode as any)
      && DomEditor.hasDOMNode(e, domSelection.focusNode as any)

    if (selectionBelongsToEditor) {
      domSelection.removeAllRanges()
    }

    if (selection) {
      deselect()
    }
  }

  // 移动光标
  e.move = (distanceOrOptions?: number | MoveOptions, reverse = false) => {
    if (typeof distanceOrOptions !== 'number') {
      move(distanceOrOptions)
      return
    }

    if (!distanceOrOptions) { return }
    if (distanceOrOptions < 0) { return }

    move({
      distance: distanceOrOptions,
      unit: 'character',
      reverse,
    })
  }

  // 反向移动光标
  e.moveReverse = (distance: number) => {
    e.move(distance, true)
  }

  /**
   * 还原选区
   */
  e.restoreSelection = () => {
    const selection = EDITOR_TO_SELECTION.get(e)

    if (selection == null) { return }

    e.focus()
    select(selection)
  }

  /**
   * 获取选区的 position
   */
  e.getSelectionPosition = () => {
    return getPositionBySelection(e)
  }

  /**
   * 获取 node 的 position
   */
  e.getNodePosition = (node: Node) => {
    return getPositionByNode(e, node)
  }

  /**
   * 是否全选
   */
  e.isSelectedAll = () => {
    const { selection } = e

    if (selection == null) { return false }

    const [start1, end1] = Range.edges(selection) // 获取当前选取的开始、结束 point
    const [start2, end2] = Editor.edges(e, []) // 获取编辑器全部的开始、结束 point

    if (Point.equals(start1, start2) && Point.equals(end1, end2)) {
      return true
    }
    return false
  }

  /**
   * 全选
   */
  e.selectAll = () => {
    const start = Editor.start(e, [])
    const end = Editor.end(e, [])

    select({
      anchor: start,
      focus: end,
    })
  }

  return e
}
